import React, { createContext, useState, useEffect, useCallback } from 'react';

// API endpoint for translations
// const API_ENDPOINT = 'http://103.87.173.134:3006/api/translate';
const API_ENDPOINT = 'http://localhost:3000/api/translate';

// Default language of the application
const DEFAULT_LANGUAGE = 'en'; // English

// Available languages in the application
export const AVAILABLE_LANGUAGES = {
  'sq': 'Albanian',
  'ar': 'Arabic',
  'bs': 'Bosnian',
  'bg': 'Bulgarian',
  'zh-CN': 'Chinese (Simplified)',
  'hr': 'Croatian',
  'nl': 'Dutch',
  'en': 'English',
  'fr': 'French',
  'de': 'German',
  'el': 'Greek',
  'iw': 'Hebrew',
  'it': 'Italian',
  'mk': 'Macedonian',
  'pl': 'Polish',
  'pt': 'Portuguese',
  'ru': 'Russian',
  'sr': 'Serbian',
  'es': 'Spanish',
  'tr': 'Turkish'
};

// Create the context
export const TranslationContext = createContext();

export const TranslationProvider = ({ children }) => {
  // State for the current language
  const [currentLanguage, setCurrentLanguage] = useState(DEFAULT_LANGUAGE);
  
  // Translation cache to avoid redundant API calls
  const [translationCache, setTranslationCache] = useState({});

  // Initialize with the stored language from localStorage
  useEffect(() => {
    const storedLanguage = localStorage.getItem('selectedLanguage');
    if (storedLanguage && AVAILABLE_LANGUAGES[storedLanguage]) {
      setCurrentLanguage(storedLanguage);
    }
  }, []);

  // Function to get a translation
  const translate = useCallback(async (text, targetLang = currentLanguage) => {
    // Don't translate if it's already in the target language or if it's empty
    if (targetLang === DEFAULT_LANGUAGE || !text || text.trim() === '') {
      return text;
    }
    
    // Generate a cache key
    const cacheKey = `${text}|${targetLang}`;
    
    // Check if we already have this translation cached
    if (translationCache[cacheKey]) {
      return translationCache[cacheKey];
    }
    
    try {
      
      // Call the translation API
      const response = await fetch(API_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          text: text,
          targetLanguage: targetLang
        })
      });
      
      if (!response.ok) {
        throw new Error('Translation request failed');
      }
      
      const data = await response.json();
      
      if (data.success && data.translation) {
        
        // Cache the result
        setTranslationCache(prevCache => ({
          ...prevCache,
          [cacheKey]: data.translation
        }));
        
        return data.translation;
      } else {
        console.error('Translation API error:', data);
        return text; // Return original if translation failed
      }
    } catch (error) {
      console.error('Error translating text:', error);
      return text; // Return original text if there's an error
    }
  }, [currentLanguage, translationCache]);

  // Function to translate multiple texts at once
  const translateBatch = useCallback(async (texts, targetLang = currentLanguage) => {
    if (targetLang === DEFAULT_LANGUAGE) {
      return texts;
    }
    
    const uniqueTexts = [...new Set(texts)].filter(text => text && text.trim() !== '');
    const results = await Promise.all(
      uniqueTexts.map(text => translate(text, targetLang))
    );
    
    // Create a mapping from original text to translated text
    const translationMap = {};
    uniqueTexts.forEach((text, index) => {
      translationMap[text] = results[index];
    });
    
    // Map the original array to translations
    return texts.map(text => text ? translationMap[text] || text : text);
  }, [currentLanguage, translate]);

  // Function to set the current language
  const setLanguage = useCallback((langCode) => {
    if (AVAILABLE_LANGUAGES[langCode]) {
      localStorage.setItem('selectedLanguage', langCode);
      setCurrentLanguage(langCode);
      return true;
    }
    return false;
  }, []);

  // The context value that will be provided
  const contextValue = {
    currentLanguage,
    setLanguage,
    translate,
    translateBatch,
    languages: AVAILABLE_LANGUAGES
  };

  return (
    <TranslationContext.Provider value={contextValue}>
      {children}
    </TranslationContext.Provider>
  );
};